import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { PageTitle } from '../../components/PageTitle';
import { AnimatedBox } from '../../components/AnimatedBox';
import { EmptyState } from '../../components/EmptyState';
import { LoadingState } from '../../components/LoadingState';
import NoticeDialog from '../../components/NoticeDialog';
import { NoticeTable } from '../../components/NoticeTable';
import { DialogMode } from '../../type/DialogMode';
import { Enterprise, ENTERPRISE_COLLECTION, User, USER_COLLECTION, type Notice } from '../../type';
import { AddNoticeButton } from '../../components/AddNoticeButton';
import { collection, getFirestore, onSnapshot, orderBy, query } from 'firebase/firestore';

interface NoticeContentProps {
  notices: Notice[];
  loading: boolean;
  error: string | null;
  addNotice: (notice: Notice) => Promise<void>;
  updateNotice: (notice: Notice) => Promise<void>;
  deleteNotice: (noticeId: string) => Promise<void>;
}

const NoticeContent = ({
  notices,
  loading,
  error,
  addNotice,
  updateNotice,
  deleteNotice,
}: NoticeContentProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(DialogMode.CREATE);
  const [selectedNotice, setSelectedNotice] = useState<Notice | undefined>(undefined);
  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);

  useEffect(() => {
    const db = getFirestore();
    const usersRef = collection(db, USER_COLLECTION);
    const q = query(usersRef, orderBy('createdAt', 'desc'));

    onSnapshot(q, (snapshot) => {
      const i_users: User[] = [];
      const e_users: User[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as User;
        if (data.uid?.length > 0 && data.displayName?.length > 0) {
          if (data.isEnterpriseUser && data.enterpriseId) {
            e_users.push(data);
          } else {
            i_users.push(data);
          }
        }
      });

      setIndependentUsers(i_users);
      setEnterpriseUsers(e_users);
    });

    const enterprisesRef = collection(db, ENTERPRISE_COLLECTION);
    const eq = query(enterprisesRef, orderBy('createdAt', 'desc'));

    onSnapshot(eq, (snapshot) => {
      const list: Enterprise[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as Enterprise;
        list.push(data);
      });

      setEnterprises(list);
    });
  }, []);

  const handleAddNotice = () => {
    setDialogMode(DialogMode.CREATE);
    setSelectedNotice(undefined);
    setIsDialogOpen(true);
  };

  const handleEditNotice = (notice: Notice) => {
    setDialogMode(DialogMode.EDIT);
    setSelectedNotice(notice);
    setIsDialogOpen(true);
  };

  const handleSaveNotice = async (notice: Notice) => {
    try {
      if (dialogMode === DialogMode.EDIT) {
        await updateNotice(notice);
      } else {
        await addNotice(notice);
      }
      handleCloseDialog();
    } catch (error) {
      alert('공지사항 저장에 실패했습니다.');
    }
  };

  const handleDeleteNotice = async (noticeId: string) => {
    try {
      const confirmed = window.confirm('정말로 이 공지사항을 삭제하시겠습니까?');
      if (confirmed) {
        await deleteNotice(noticeId);
        handleCloseDialog();
      }
    } catch (error) {
      alert('공지사항 삭제에 실패했습니다.');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedNotice(undefined);
  };

  if (loading) {
    return (
      <AnimatedBox>
        <PageTitle>공지사항 관리</PageTitle>
        <LoadingState />
      </AnimatedBox>
    );
  }

  return (
    <AnimatedBox>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3 
      }}>
        <PageTitle>공지사항 관리</PageTitle>
        <AddNoticeButton title="새 공지사항 추가" onAddClick={handleAddNotice} />
      </Box>

      {error && (
        <Box sx={{ mb: 3, color: 'error.main' }}>
          {error}
        </Box>
      )}

      {notices.length === 0 ? (
        <EmptyState message="아직 등록된 공지사항이 없습니다." />
      ) : (
        <NoticeTable
          data={notices}
          onNoticeSelect={handleEditNotice}
        />
      )}
      {isDialogOpen && (
        <NoticeDialog
          open={isDialogOpen}
          mode={dialogMode}
          notice={selectedNotice}
          onClose={handleCloseDialog}
          onSave={handleSaveNotice}
          onDelete={handleDeleteNotice}
          i_users={independentUsers}
          e_users={enterpriseUsers}
          ent={enterprises}
        />
      )}
    </AnimatedBox>
  );
};

export default NoticeContent; 